// src/telemetry/TelemetryManager.ts

import { eventQueue } from './queue/EventQueue'

import { batchUploader } from './queue/BatchUploader'

import type { TelemetryEvent, TelemetryFocus } from './types/telemetry'

class TelemetryManager {
  private focus: TelemetryFocus | null = null

  private sessionId: string

  private readonly sessionStartedAt: number

  private readonly visitFrequencyKey = '__telemetry_module_visits__'

  private currentActivePage: TelemetryEvent | null = null

  private pageStartTime: number = 0

  constructor () {
    const existing = localStorage.getItem('__telemetry_session__')
    const existingStartedAt = sessionStorage.getItem('__telemetry_started_at__')

    if (existing) {
      this.sessionId = existing
      this.sessionStartedAt = existingStartedAt ? Number(existingStartedAt) : Date.now()
    } else {
      this.sessionStartedAt = Date.now()
      this.sessionId = `sess_${this.sessionStartedAt}_${crypto.randomUUID()}`
      localStorage.setItem('__telemetry_session__', this.sessionId)
      sessionStorage.setItem('__telemetry_started_at__', String(this.sessionStartedAt))
    }
  }

  resetSession () {
    const ts = Date.now();
    this.sessionId = `sess_${ts}_${crypto.randomUUID()}`;
    // Explicitly update session start time to now
    Object.defineProperty(this, 'sessionStartedAt', { value: ts, writable: true });
    localStorage.setItem('__telemetry_session__', this.sessionId);
    sessionStorage.setItem('__telemetry_started_at__', String(ts));
  }

  private getBrowserInfo () {
    return navigator.userAgent
  }

  private getDeviceInfo () {
    return navigator.platform
  }

  private getSessionDurationSeconds () {
    return Math.max(0, Math.floor((Date.now() - this.sessionStartedAt) / 1000))
  }

  private recordModuleVisit (moduleName: string) {
    try {
      const raw = sessionStorage.getItem(this.visitFrequencyKey)
      const visits = raw ? JSON.parse(raw) as Record<string, number> : {}
      visits[moduleName] = (visits[moduleName] || 0) + 1
      sessionStorage.setItem(this.visitFrequencyKey, JSON.stringify(visits))
      return visits
    } catch {
      return { [moduleName]: 1 }
    }
  }

  getSessionId () {
    return this.sessionId
  }
  getFocus () {
    return this.focus
  }
  setFocus (focus: TelemetryFocus) {
    this.focus = focus
  }

  track (partial: Omit<TelemetryEvent, 'id' | 'timestamp' | 'sessionId'>) {
    const isPageView = partial.eventType === 'page_view' || partial.actionType === 'page_view';

    if (isPageView) {
      const currentRoute = partial.routePath;

      // Prevent duplicate page view tracking from React StrictMode or double mounts
      if (this.currentActivePage && this.currentActivePage.routePath === currentRoute) {
        return;
      }

      // 1. If there is a previous active page, calculate its duration and send it
      if (this.currentActivePage) {
        const duration = Math.max(0, Math.floor((Date.now() - this.pageStartTime) / 1000));
        const updatedPreviousEvent: TelemetryEvent = {
          ...this.currentActivePage,
          activeDurationSeconds: duration,
          sessionDurationSeconds: this.getSessionDurationSeconds(),
        };

        // Existing frontend telemetry/logging
        eventQueue.add(updatedPreviousEvent);

        // Backend /telemetry/event API call
        void batchUploader.uploadEvent(updatedPreviousEvent);
      }

      // 2. Set the new page view event as active and save entry time
      const event: TelemetryEvent = {
        ...partial,
        id: crypto.randomUUID(),
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        deviceInfo: this.getDeviceInfo(),
        browserInfo: this.getBrowserInfo(),
        sessionDurationSeconds:
          partial.sessionDurationSeconds ?? this.getSessionDurationSeconds(),
        activeSessionState:
          partial.activeSessionState ?? { state: 'active' },
        moduleVisitFrequencies:
          partial.moduleVisitFrequencies ?? this.recordModuleVisit(partial.moduleName),
        metadata: {
          ...(partial.metadata || {}),
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          language: navigator.language,
          timezone:
            Intl.DateTimeFormat()
              .resolvedOptions()
              .timeZone,
        },
      };

      this.currentActivePage = event;
      this.pageStartTime = Date.now();

    } else {
      // It's a non-page_view event (e.g. action, form, error)
      const event: TelemetryEvent = {
        ...partial,
        id: crypto.randomUUID(),
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        deviceInfo: this.getDeviceInfo(),
        browserInfo: this.getBrowserInfo(),
        sessionDurationSeconds:
          partial.sessionDurationSeconds ?? this.getSessionDurationSeconds(),
        activeSessionState:
          partial.activeSessionState ?? { state: 'active' },
        metadata: {
          ...(partial.metadata || {}),
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          language: navigator.language,
          timezone:
            Intl.DateTimeFormat()
              .resolvedOptions()
              .timeZone,
        },
      };

      // Existing frontend telemetry/logging
      eventQueue.add(event);

      // Backend /telemetry/event API call
      void batchUploader.uploadEvent(event);
    }
  }

  unload () {
    if (this.currentActivePage) {
      const duration = Math.max(0, Math.floor((Date.now() - this.pageStartTime) / 1000));
      const updatedEvent: TelemetryEvent = {
        ...this.currentActivePage,
        activeDurationSeconds: duration,
        sessionDurationSeconds: this.getSessionDurationSeconds(),
      };

      // Existing frontend telemetry/logging
      eventQueue.add(updatedEvent);

      // Backend /telemetry/event API call
      void batchUploader.uploadEvent(updatedEvent);

      this.currentActivePage = null;
    }
  }

  async flush () {
    // TODO/DEPRECATED: flush is deprecated in favor of immediate event-driven uploads to /telemetry/event.
    /*
    const events = eventQueue.getAll()

    if (!events.length) return

    const success = await batchUploader.upload(this.sessionId, events)

    if (success) {
      eventQueue.clear()
    }
    */
  }
}

export const telemetryManager = new TelemetryManager()
